import type { TFunction, TFunctionDetailedResult, TOptions } from 'i18next';
import { useTranslation } from 'react-i18next';

function useArrayTranslation<T>({
  t: tFromProps,
  i18nKey,
  options,
}: Omit<Props<T>, 'mapperFunction'>): string | T[] | TFunctionDetailedResult<T[]> {
  const { t: tGlob } = useTranslation();

  if (i18nKey === undefined) {
    return '';
  }

  const t = tFromProps ?? tGlob;
  return t<string, T[], TOptions>(i18nKey, { ...options, returnObjects: true });
}

interface Props<T> {
  i18nKey: string;
  t?: TFunction;
  options?: TOptions;
  mapperFunction: (args: T, index: number) => JSX.Element;
}

/**
 * When in cimode strings are returned for all transaltions. In combination with
 * returnObjects = true this breaks the page. This is a wrapper to use which returns
 * the string translation in cimode instead of breaking.
 * Also see issue: https://github.com/i18next/i18next/issues/1134
 */
export function ArrayTranslation<T>({
  i18nKey,
  t,
  options,
  mapperFunction,
}: Props<T>): JSX.Element {
  let translation = useArrayTranslation({ t, i18nKey, options });
  if (options?.returnDetails === true) {
    translation = (translation as TFunctionDetailedResult<T[]>).res;
  }

  if (!Array.isArray(translation)) return <>{translation}</>;

  return <>{translation.map(mapperFunction)}</>;
}
